<soft-ds-card
  class="ds-kpi"
  [class.ds-kpi--clickable]="clickable()"
  [attr.role]="clickable() ? 'button' : null"
  [attr.tabindex]="clickable() ? 0 : null"
  [style.--ds-kpi-accent-resolved]="accentColor()"
  (click)="onCardClick()"
  (keydown.enter)="onCardClick()"
  (keydown.space)="$event.preventDefault(); onCardClick()"
>
  @if (loading()) {
    <soft-ds-card-header>
      @if (hasLeading()) {
        <soft-ds-card-leading>
          <soft-ds-loading-skeleton class="ds-kpi-skeleton-icon" />
        </soft-ds-card-leading>
      }
      <soft-ds-card-title variant="overline">{{ title() }}</soft-ds-card-title>
      @if (showBadge()) {
        <soft-ds-card-trailing>
          <soft-ds-loading-skeleton class="ds-caption ds-kpi-skeleton-badge" />
        </soft-ds-card-trailing>
      }
    </soft-ds-card-header>

    <soft-ds-card-body
      class="ds-kpi-values"
      [class.ds-kpi-values--inline]="comparisonPosition() === 'inline'"
    >
      <soft-ds-loading-skeleton class="ds-heading-3 ds-kpi-skeleton-value" />
      <soft-ds-loading-skeleton class="ds-caption ds-kpi-skeleton-comparison" />
    </soft-ds-card-body>

    @if (effectiveVariant() === 'bar' || effectiveVariant() === 'segmented') {
      <soft-ds-card-footer class="ds-kpi-progress-section">
        <soft-ds-progress-bar
          [variant]="effectiveVariant() === 'segmented' ? 'segmented' : 'percent'"
          [showLegend]="effectiveVariant() === 'segmented'"
          [loading]="true"
        />
        @if (effectiveVariant() === 'bar') {
          <soft-ds-loading-skeleton class="ds-caption ds-kpi-skeleton-progress" />
        }
      </soft-ds-card-footer>
    } @else if (effectiveVariant() === 'sparkline') {
      <soft-ds-loading-skeleton class="ds-kpi-skeleton-sparkline" />
    }
  } @else {
    <soft-ds-card-header>
      @if (hasLeading()) {
        <soft-ds-card-leading>
          @if (abbreviation(); as abbr) {
            <span class="ds-kpi-abbreviation">{{ abbr }}</span>
          } @else if (icon(); as iconName) {
            <span class="ds-kpi-icon">
              <soft-ds-icon [name]="iconName" />
            </span>
          }
        </soft-ds-card-leading>
      }
      <soft-ds-card-title variant="overline">{{ title() }}</soft-ds-card-title>
      @if (shouldShowToggle() || (showBadge() && deltaPercent() !== null)) {
        <soft-ds-card-trailing>
          @if (shouldShowToggle()) {
            <soft-ds-button
              type="button"
              variant="outline"
              mode="iconOnly"
              size="sm"
              class="ds-kpi-variant-toggle"
              [class.is-active]="isToggleActive()"
              [attr.aria-label]="toggleAriaLabel()"
              [attr.aria-pressed]="isToggleActive()"
              (click)="toggleVariant()"
            >
              <soft-ds-icon [name]="toggleIcon()" />
            </soft-ds-button>
          }
          @if (showBadge() && deltaPercent() !== null) {
            @let delta = deltaPercent()!;
            @let positive = delta >= 0;
            <soft-ds-badge
              class="ds-kpi-delta-badge"
              [variant]="deltaIsGood() ? 'success' : 'danger'"
              [softDeltaFlip]="delta"
              [animate]="animateValue()"
            >
              @if (badgeFormatFn(); as fn) {
                {{ fn(delta, resolvedValue(), resolvedPrevValue()!) }}
              } @else {
                {{ positive ? '+' : '' }}{{ delta | number: '1.2-2' }}%
              }
            </soft-ds-badge>
          }
        </soft-ds-card-trailing>
      }
    </soft-ds-card-header>

    <soft-ds-card-body
      class="ds-kpi-values"
      [class.ds-kpi-values--inline]="comparisonPosition() === 'inline'"
    >
      @if (cleared()) {
        <div class="ds-kpi-cleared-block">
          <div class="ds-kpi-cleared">
            <i
              class="fa-regular fa-check ds-kpi-cleared-icon"
              aria-hidden="true"
            ></i>
            <span class="ds-kpi-cleared-title">{{ clearedLabel() }}</span>
          </div>
          @if (clearedSummary(); as summary) {
            <span class="ds-caption ds-kpi-caption">{{ summary }}</span>
          }
        </div>
      } @else {
        <span
          class="ds-heading-3 ds-kpi-value"
          [softAnimatedNumber]="resolvedValue()"
          [format]="formatNumber()"
          [animate]="animateValue()"
        ></span>
        <span
          class="ds-caption ds-kpi-caption"
          [class.is-empty]="!hasComparison() && displayDifference() === null"
          [attr.aria-hidden]="hasComparison() || displayDifference() !== null ? null : true"
        >
          @if (hasComparison()) {
            @if (comparisonMode() === 'delta' && deltaPercent() !== null) {
              <span
                class="ds-kpi-delta-text"
                [class.is-up]="deltaIsGood()"
                [class.is-down]="!deltaIsGood()"
              >{{ deltaText() }}</span>
              {{ resolvedComparisonLabel() }}
            } @else {
              {{ resolvedComparisonLabel() }}@if (displayPrevValue()) {{{ comparisonSeparator() }} {{ displayPrevValue() }}}
            }
          } @else {
            &nbsp;
          }
          @if (displayDifference(); as diff) {
            <span
              class="ds-kpi-difference"
              [class.is-up]="differenceIsGood()"
              [class.is-down]="!differenceIsGood()"
            >{{ diff }}</span>
          }
        </span>
      }
    </soft-ds-card-body>

    @if (effectiveVariant() === 'sparkline' && sparklinePath(); as path) {
      <svg
        class="ds-kpi-sparkline"
        viewBox="0 0 100 32"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient [attr.id]="gradientId" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stop-color="currentColor" stop-opacity="0.35" />
            <stop offset="100%" stop-color="currentColor" stop-opacity="0" />
          </linearGradient>
        </defs>
        <path
          class="ds-kpi-sparkline-area"
          [attr.d]="path.area"
          [attr.fill]="'url(#' + gradientId + ')'"
        ></path>
        <path class="ds-kpi-sparkline-line" [attr.d]="path.line"></path>
      </svg>
    }

    @if (
      !cleared() &&
      effectiveVariant() === 'bar' &&
      progressPercent() !== null
    ) {
      <soft-ds-card-footer class="ds-kpi-progress-section">
        <soft-ds-progress-bar
          variant="percent"
          [value]="progressClamped()"
          [color]="progressFillColor()"
          [markerPercent]="progressScale() < 1 ? 100 : null"
          [scale]="progressScale()"
        />
        <span class="ds-caption ds-kpi-caption">
          {{ progressPercent() | number: '1.2-2' }}% {{ progressLabel() }}
        </span>
      </soft-ds-card-footer>
    } @else if (!cleared() && effectiveVariant() === 'segmented') {
      <soft-ds-card-footer class="ds-kpi-progress-section">
        <soft-ds-progress-bar
          variant="segmented"
          [showLegend]="true"
          [segments]="resolvedSegments()"
          [clickableSegments]="clickableSegments()"
          (segmentClick)="segmentClick.emit($event)"
          [attr.aria-label]="title()"
        />
      </soft-ds-card-footer>
    }
  }
</soft-ds-card>
